/**
 * Shared layout for AI Resume Optimizer step cards.
 * Use these classes so every step has the same size, radius, and responsive behavior.
 * Steps are viewport-constrained: card scrolls inside, nav stays visible on all devices.
 * Mobile/tablet: full width, safe-area insets, 44px touch targets, sticky nav.
 */

/** Outer wrapper: full width, flex column, no scroll – card scrolls, nav stays visible.
 *  mx-auto ensures consistent centre-alignment on wide screens for every step. */
export const STEP_WRAPPER_CLASS =
  'w-full min-w-0 max-w-full lg:max-w-5xl mx-auto px-3 xs:px-4 sm:px-5 lg:px-8 flex-1 flex flex-col min-h-0 overflow-hidden gap-0';

/** Motion container: fill flow height so card scrolls and nav stays visible; tight vertical padding. */
export const STEP_MOTION_CLASS =
  'h-full min-h-0 flex flex-col py-2 sm:py-3';

/** Content area – fills available space; nav is in flow below so no extra bottom padding. */
export const STEP_SCROLL_AREA_CLASS =
  'flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden scrollbar-hide [-webkit-overflow-scrolling:touch] pb-2';

/** Bottom nav: in document flow (not sticky). */
export const STEP_NAV_CLASS =
  'flex-shrink-0 z-10 w-full flex justify-between items-center gap-2 sm:gap-3 pt-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] mt-2 border-t border-gray-200 dark:border-gray-600 px-3 xs:px-4 sm:px-0 max-w-full lg:max-w-5xl lg:mx-auto';

/**
 * Main card: same size and look on all steps and devices.
 * min-h ensures no zig-zag when navigating; rounded and shadow are consistent.
 */
export const STEP_CARD_CLASS =
  'bg-white dark:bg-gray-800 rounded-2xl md:rounded-[1.5rem] shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex-1 flex flex-col min-h-[320px] sm:min-h-[420px] md:min-h-[460px]';

/**
 * Compact card: content-sized (no flex-1) so no empty space inside card; nav sits right below.
 */
export const STEP_CARD_COMPACT_CLASS =
  'bg-white dark:bg-gray-800 rounded-2xl md:rounded-[1.5rem] shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col min-w-0 w-full max-w-full';
